import React from 'react';
import { useAppStore } from '../stores/appStore';
import { useAllCards, useCreateCard, useDeleteCard } from '../hooks/useCards';
import { ErrorMessage } from './ErrorMessage';

export const AdminView: React.FC = () => {
  const { newWord, newDefinition, updateForm, clearForm } = useAppStore();
  const { data: response, isLoading, error } = useAllCards();
  const createCardMutation = useCreateCard();
  const deleteCardMutation = useDeleteCard();

  const handleCreateCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWord.trim() || !newDefinition.trim() || createCardMutation.isPending) return;

    try {
      await createCardMutation.mutateAsync({
        word: newWord.trim(),
        definition: newDefinition.trim(),
      });
      clearForm();
    } catch (error) {
      console.error('Failed to create card:', error);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!confirm('Are you sure you want to delete this card?')) return;

    try {
      await deleteCardMutation.mutateAsync(cardId);
    } catch (error) {
      console.error('Failed to delete card:', error);
    }
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW': return 'bg-blue-100 text-blue-800';
      case 'READY': return 'bg-green-100 text-green-800';
      case 'WAITING': return 'bg-yellow-100 text-yellow-800';
      case 'MASTERED': return 'bg-purple-100 text-purple-800';
      case 'HARD': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const cards = response || [];
  const hasError = error || createCardMutation.error || deleteCardMutation.error;
  const errorMessage = (error as any)?.message || 
                      (createCardMutation.error as any)?.message || 
                      (deleteCardMutation.error as any)?.message;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Manage Cards</h1>
        <p className="text-gray-600">Create new cards and view existing ones</p>
      </div>

      {/* Error Display */}
      {hasError && <ErrorMessage error={errorMessage} className="mb-6" />}

      {/* Create Card Form */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6 border-b">
          <h2 className="text-lg font-medium text-gray-900">Create New Card</h2>
        </div>
        <form onSubmit={handleCreateCard} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="word" className="block text-sm font-medium text-gray-700 mb-2">
                Word/Term
              </label>
              <input
                type="text"
                id="word"
                value={newWord}
                onChange={(e) => updateForm('word', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter word or term..."
                required
              />
            </div>
            <div>
              <label htmlFor="definition" className="block text-sm font-medium text-gray-700 mb-2">
                Definition
              </label>
              <input
                type="text"
                id="definition"
                value={newDefinition}
                onChange={(e) => updateForm('definition', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter definition..."
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={createCardMutation.isPending || !newWord.trim() || !newDefinition.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createCardMutation.isPending ? 'Creating...' : 'Create Card'}
          </button>
        </form>
      </div>

      {/* Cards List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-lg font-medium text-gray-900">
            All Cards ({cards.length})
          </h2>
        </div>

        {isLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading cards...</p>
          </div>
        ) : cards.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No cards found. Create your first card above!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Word
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Definition
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Errors
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Next Review
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cards.map((card: any) => (
                  <tr key={card.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{card.word}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700 max-w-xs truncate">{card.definition}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(card.status)}`}>
                        {card.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {card.current_bin}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {card.incorrect_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {card.timeUntilReview}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        disabled={deleteCardMutation.isPending}
                        className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                      >
                        {deleteCardMutation.isPending ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};